import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Layout from "./components/Layout";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import CreateHandoff from "./pages/CreateHandoff";
import HandoffDetails from "./pages/HandoffDetails";
import TaskHistory from "./pages/TaskHistory";
import TeamManagement from "./pages/TeamManagement";
import Archive from "./pages/Archive";
import OrganizationSettings from "./pages/OrganizationSettings";
import NotificationPreferences from "./pages/NotificationPreferences";
import DivisionTeam from "./pages/DivisionTeam";
import Schedule from "./pages/Schedule";
import PilotFeedback from "./pages/PilotFeedback";
import MyFeedback from "./pages/MyFeedback";
import Organizations from "./pages/Organizations";
import SetPassword from "./pages/SetPassword";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Advisories from "./pages/Advisories";
import AdvisoryDetails from "./pages/AdvisoryDetails";
import { canManageRecords, isAdminRole } from "./lib/access";

function SetPasswordGate() {
  const { user, loading } = useAuth();
  if (loading || user === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (!user.must_change_password) return <Navigate to="/dashboard" replace />;
  return <SetPassword />;
}

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_change_password) return <Navigate to="/set-password" replace />;
  return <Layout>{children}</Layout>;
}

function ManageOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_change_password) return <Navigate to="/set-password" replace />;
  if (!canManageRecords(user)) return <Navigate to="/dashboard" replace />;
  return <Layout>{children}</Layout>;
}

function AdminOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_change_password) return <Navigate to="/set-password" replace />;
  if (!isAdminRole(user)) return <Navigate to="/dashboard" replace />;
  return <Layout>{children}</Layout>;
}

function PilotFeedbackOnly({ children, admin }) {
  const { user, loading } = useAuth();
  if (loading || user === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_change_password) return <Navigate to="/set-password" replace />;
  if (!user.pilot_feedback_enabled) return <Navigate to="/dashboard" replace />;
  if (admin && !isAdminRole(user)) return <Navigate to="/dashboard" replace />;
  return <Layout>{children}</Layout>;
}

function SystemOwnerOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_change_password) return <Navigate to="/set-password" replace />;
  if (user.role !== "system_admin") return <Navigate to="/dashboard" replace />;
  return <Layout>{children}</Layout>;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/set-password" element={<SetPasswordGate />} />
      <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
      <Route path="/handoffs/new" element={<Protected><CreateHandoff /></Protected>} />
      <Route path="/handoffs/:id/edit" element={<Protected><CreateHandoff /></Protected>} />
      <Route path="/handoffs/:id" element={<Protected><HandoffDetails /></Protected>} />
      <Route path="/tasks" element={<Protected><TaskHistory /></Protected>} />
      <Route path="/team" element={<AdminOnly><TeamManagement /></AdminOnly>} />
      <Route path="/division-team" element={<ManageOnly><DivisionTeam /></ManageOnly>} />
      <Route path="/archive" element={<Protected><Archive /></Protected>} />
      <Route path="/schedule" element={<Protected><Schedule /></Protected>} />
      <Route path="/advisories" element={<Protected><Advisories /></Protected>} />
      <Route path="/advisories/:id" element={<Protected><AdvisoryDetails /></Protected>} />
      <Route path="/pilot-feedback" element={<PilotFeedbackOnly admin><PilotFeedback /></PilotFeedbackOnly>} />
      <Route path="/my-feedback" element={<PilotFeedbackOnly><MyFeedback /></PilotFeedbackOnly>} />
      <Route path="/settings" element={<AdminOnly><OrganizationSettings /></AdminOnly>} />
      <Route path="/organizations" element={<SystemOwnerOnly><Organizations /></SystemOwnerOnly>} />
      <Route path="/preferences" element={<Protected><NotificationPreferences /></Protected>} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
